import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Copy, Pencil, Trash2, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeService } from "@/lib/calc";
import { SectionCard } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ServicesPage() {
  const { t, user, fmt } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then((r) => r.data) });
  const [toDelete, setToDelete] = useState(null);

  const duplicate = async (id) => {
    await api.post(`/services/${id}/duplicate`);
    qc.invalidateQueries({ queryKey: ["services"] });
    toast.success(t("services.duplicated"));
  };

  const confirmDelete = async () => {
    await api.delete(`/services/${toDelete}`);
    setToDelete(null);
    qc.invalidateQueries({ queryKey: ["services"] });
    toast.success(t("services.deleted"));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("services.title")}</h1>
          <p className="mt-1 text-brand-taupe">{t("services.subtitle")}</p>
        </div>
        <Button onClick={() => navigate("/services/new")} data-testid="services-new-button" className="rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 h-12 px-5">
          <Plus className="mr-2 h-5 w-5" /> <span className="hidden sm:inline">{t("services.new")}</span>
        </Button>
      </div>

      {services.length === 0 ? (
        <SectionCard accent className="text-center py-14">
          <h3 className="font-serif text-2xl text-brand-anthracite">{t("services.empty")}</h3>
          <p className="mt-2 text-brand-taupe max-w-sm mx-auto">{t("services.emptyDesc")}</p>
          <Button onClick={() => navigate("/services/new")} data-testid="services-empty-cta" className="mt-6 rounded-xl bg-brand-anthracite text-brand-ivory">{t("services.emptyCta")}</Button>
        </SectionCard>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {services.map((s) => {
            const c = computeService(s, user);
            return (
              <SectionCard key={s.id} className="!p-5" data-testid={`service-card-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => navigate(`/services/${s.id}`)} className="text-left flex-1">
                    <h3 className="font-serif text-lg font-medium text-brand-anthracite">{s.name}</h3>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-brand-taupe"><Clock className="h-3.5 w-3.5" /> {c.effectiveMin.toFixed(0)} {t("common.min")}</div>
                  </button>
                  <span className="rounded-full bg-brand-blush/40 border border-brand-taupe/20 px-3 py-1 text-xs font-medium">{c.margin.toFixed(0)}%</span>
                </div>
                <div className="mt-4 flex items-end justify-between border-t border-brand-line pt-4">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-brand-taupe">{t("services.recommended")}</div>
                    <div className="font-semibold text-xl text-brand-anthracite tabular-nums">{fmt(c.commercial.nice || c.finalPrice)}</div>
                    {s.current_price ? <div className="text-xs text-brand-taupe">{t("services.current")}: {fmt(s.current_price)}</div> : <div className="text-xs text-brand-taupe">{t("services.noPrice")}</div>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => navigate(`/services/${s.id}`)} data-testid={`service-edit-${s.id}`} className="rounded-lg p-2 text-brand-taupe hover:bg-brand-sand hover:text-brand-anthracite"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => duplicate(s.id)} data-testid={`service-duplicate-${s.id}`} className="rounded-lg p-2 text-brand-taupe hover:bg-brand-sand hover:text-brand-anthracite"><Copy className="h-4 w-4" /></button>
                    <button onClick={() => setToDelete(s.id)} data-testid={`service-delete-${s.id}`} className="rounded-lg p-2 text-brand-taupe hover:bg-[#B85B5B]/10 hover:text-[#B85B5B]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white border-brand-line">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">{t("services.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("services.deleteConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm" className="bg-[#B85B5B] hover:bg-[#a24f4f]">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
